/**
 * Responsive sizing helpers for the matches-widget rail.
 *
 * Both `AllMatches` and `TeamMatches` cap the rail at a different
 * number of match cells depending on the embed's available width.
 * The breakpoints + counts live here so the two variants stay in
 * lockstep.
 *
 * `appState.windowWidth` is kept in sync with `window.innerWidth` by
 * the `(embeds)` layout, so anything wrapping these helpers in
 * `$derived` automatically re-runs when the host page resizes.
 */

import { appState } from '$lib/stores/index.svelte';

/**
 * Mobile viewport breakpoint (px). At/below this width the rail
 * switches to its tighter window so the embed stays readable inside
 * narrow third-party columns.
 */
export const MOBILE_BREAKPOINT_PX = 600;

/** Window size on mobile-width embeds. */
export const MAX_MATCHES_MOBILE = 5;
/** Window size on desktop-width embeds. */
export const MAX_MATCHES_DESKTOP = 10;

/** `true` when the rail should use its mobile sizing. */
export const isMobile = () => appState.windowWidth <= MOBILE_BREAKPOINT_PX;

/**
 * Total number of match cells to show in the rail, responsive to the
 * current viewport. Call this from inside a `$derived` so it
 * recomputes on resize.
 */
export const getMaxMatches = () =>
  isMobile() ? MAX_MATCHES_MOBILE : MAX_MATCHES_DESKTOP;

/**
 * For the team-page variant: how many slots to reserve for upcoming
 * matches after the pivot (the team's next live/upcoming match).
 * The rest of the window — minus the pivot itself — is filled with
 * already-played matches on the left. The ratio is biased so the
 * rail lands on upcoming fixtures by default, with just enough
 * recent results for context.
 */
export const getUpcomingAfterPivot = () => (isMobile() ? 1 : 3);
